/**
 * PostDetailPage — single post view.
 *
 * Works for both authenticated and anonymous callers. When `isPublic=true`
 * the top nav is a lightweight landing bar; otherwise nothing extra is
 * rendered (AppShell provides its own chrome).
 *
 * Uses `/api/publish/posts/{id}` when authenticated (returns viewer state)
 * and falls back to `/api/publish/public/posts/{id}` (with server-rendered
 * comments) for anonymous visitors.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, House } from "@phosphor-icons/react";
import { PostCard } from "@/components/publish/PostCard";
import { PostComposer } from "@/components/publish/PostComposer";
import { CommentList } from "@/components/publish/CommentList";
import { ReportDialog } from "@/components/publish/ReportDialog";
import {
  getPost, publicPost, setReaction, removeReaction, toggleBookmark,
  incrementView, deletePost, sharePost, delinkPost, republishPost,
} from "@/lib/publishApi";
import { formatApiError } from "@/lib/api";

import { devWarn } from "@/lib/devLog";
export default function PostDetailPage({ isPublic = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const authed = !!user;

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);
  const [editingOpen, setEditingOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      if (authed && !isPublic) {
        const p = await getPost(id);
        setPost(p);
      } else {
        const { post: p, comments } = await publicPost(id);
        setPost({ ...p, viewer: { is_author: false, can_moderate: false, reaction: null, bookmarked: false, reported: false }, _comments: comments });
      }
      incrementView(id);
      // Update page title & meta for SEO on public pages
      if (isPublic && document?.title) {
        // Set later once we have the post
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setPost(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  // SEO: update <title> + description for public route
  useEffect(() => {
    if (!isPublic || !post) return;
    const prevTitle = document.title;
    const title = post.title ? `${post.title} · TherapyStack Pro` : "TherapyStack Pro Publish";
    document.title = title;
    const meta = document.querySelector('meta[name="description"]')
      || document.head.appendChild(Object.assign(document.createElement("meta"), { name: "description" }));
    const desc = post.excerpt || (post.body_html || "").replace(/<[^>]+>/g, " ").slice(0, 160).trim();
    meta.setAttribute("content", desc);
    return () => { document.title = prevTitle; };
  }, [isPublic, post]);

  const onReact = async (reaction) => {
    if (!authed) { toast.info("Please log in to react"); return; }
    try {
      if (!reaction) await removeReaction(id);
      else await setReaction(id, reaction);
      const updated = await getPost(id);
      setPost(updated);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onBookmark = async () => {
    if (!authed) { toast.info("Please log in to save posts"); return; }
    try {
      const res = await toggleBookmark(id);
      setPost(prev => ({ ...prev, viewer: { ...(prev.viewer || {}), bookmarked: res.bookmarked } }));
      toast.success(res.bookmarked ? "Saved" : "Removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    try {
      await deletePost(id);
      toast.success("Deleted");
      navigate(isPublic ? "/publish" : "/app/publish");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onShare = async () => {
    const url = `${window.location.origin}/publish/${id}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      } else {
        window.prompt("Copy this link:", url);
      }
      if (authed) {
        try {
          const res = await sharePost(id);
          setPost(prev => ({ ...prev, shares_count: res.shares_count }));
        } catch (err) {
          devWarn("[publish] share counter bump failed", err);
        }
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelink = async () => {
    const msg = "Delink this post from the public Landing Page?\n\n"
      + "It will be removed from the public feed, search and topic pages. "
      + "It stays in My Posts with all comments, reactions, bookmarks and "
      + "analytics intact, and you can republish it later at the same URL.";
    if (!window.confirm(msg)) return;
    try {
      await delinkPost(id);
      toast.success("Post delinked from public feed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onRepublish = async () => {
    try {
      await republishPost(id);
      toast.success(post?.status === "delinked" ? "Post republished" : "Post published");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) {
    return (
      <div className={isPublic ? "max-w-3xl mx-auto px-4 py-10" : "space-y-4"}>
        <div className="h-96 bg-muted animate-pulse rounded-2xl" />
      </div>
    );
  }
  if (!post) {
    return (
      <div className={isPublic ? "max-w-3xl mx-auto px-4 py-16 text-center" : "text-center py-12"}>
        <p className="text-lg font-semibold">Post not found</p>
        <p className="text-sm text-muted-foreground mt-1">The post may have been removed or is not yet public.</p>
        <Button className="mt-4" onClick={() => navigate(isPublic ? "/publish" : "/app/publish")}>
          <ArrowLeft size={14} className="mr-1.5" /> Back to feed
        </Button>
      </div>
    );
  }

  return (
    <div className={isPublic ? "max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4" : "space-y-4"}
         data-testid="post-detail-page">
      {isPublic && (
        <PublicTopBar authed={authed} />
      )}
      <div>
        <button
          onClick={() => navigate(isPublic ? "/publish" : "/app/publish")}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          data-testid="post-detail-back-btn"
        >
          <ArrowLeft size={12} /> Back to feed
        </button>
      </div>

      <PostCard
        post={post}
        layout="detail"
        interactive={authed}
        viewer={post.viewer}
        onReact={onReact}
        onBookmark={onBookmark}
        onReport={() => setReportOpen(true)}
        onShare={onShare}
        onEdit={() => setEditingOpen(true)}
        onDelete={onDelete}
        onDelink={onDelink}
        onRepublish={onRepublish}
      />

      <div className="border border-border rounded-2xl bg-card p-4 sm:p-5">
        <CommentList
          postId={post.id}
          canWrite={authed}
          currentUserId={user?.id}
          initialComments={post._comments || null}
          readOnly={isPublic && !authed}
          onCommentsChanged={(n) => setPost(prev => ({ ...prev, comments_count: n }))}
        />
      </div>

      {reportOpen && (
        <ReportDialog
          post={post} open={reportOpen} onOpenChange={setReportOpen}
          onReported={() => load()}
        />
      )}
      {editingOpen && (
        <PostComposer
          open={editingOpen} onOpenChange={setEditingOpen}
          canPostAd={(user?.roles || []).some(r => ["owner","admin"].includes(r))}
          editingPostId={post.id}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}

function PublicTopBar({ authed }) {
  return (
    <div className="border-b border-border pb-3 mb-2 flex items-center justify-between">
      <Link to="/publish" className="font-bold tracking-tight text-lg" data-testid="publish-brand-link">
        TherapyStack Pro
      </Link>
      {authed ? (
        <Link to="/app/publish">
          <Button variant="outline" size="sm"><House size={14} className="mr-1.5" /> Back to app</Button>
        </Link>
      ) : (
        <Link to="/login"><Button variant="outline" size="sm" data-testid="publish-login-btn">Log in</Button></Link>
      )}
    </div>
  );
}
